import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Wrench, Package, Phone, Clock, Shield, Zap, Users, Settings } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import seitalLogo from '@/assets/partners/seital.png';
import { motion } from 'framer-motion';

const Support = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={t('Υποστήριξη', 'Support')}
        description={t('Τεχνική υποστήριξη και ανταλλακτικά από την TFT. Ταχεία ανταπόκριση, 60+ χρόνια εμπειρίας.', 'Technical support and spare parts from TFT. Fast response, 60+ years of experience.')}
        canonical="https://www.tft.gr/support"
      />
      <Navigation />

      <main>
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-primary to-primary-light text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold mb-6">{t('Υπηρεσίες Υποστήριξης', 'Support Services')}</h1>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              {t('Πλήρης τεχνική υποστήριξη και ανταλλακτικά για όλο τον εξοπλισμό σας', 'Complete technical support and spare parts for all your equipment')}
            </p>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {/* Spare Parts */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.45, delay: 0 }}
                whileHover={{ y: -6 }}
              >
                <Card className="h-full transition-shadow duration-300 hover:shadow-xl">
                  <CardContent className="p-8">
                    <div className="mb-6 p-4 bg-primary/10 rounded-2xl w-fit group">
                      <Package className="h-12 w-12 text-primary transition-transform duration-300 group-hover:scale-110" />
                    </div>
                    <h2 className="text-3xl font-bold mb-4">{t('Ανταλλακτικά', 'Spare Parts')}</h2>
                    <p className="text-muted-foreground mb-6">
                      {t('Διατηρούμε εκτενές απόθεμα ανταλλακτικών για όλες τις σειρές εξοπλισμού που προσφέρουμε. Εξασφαλίζουμε γρήγορη παράδοση και ανταγωνιστικές τιμές.', 'We maintain an extensive stock of spare parts for all equipment lines we offer. We ensure fast delivery and competitive prices.')}
                    </p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start">
                        <Shield className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span>{t('Γνήσια ανταλλακτικά από τους κατασκευαστές', 'Genuine parts from manufacturers')}</span>
                      </li>
                      <li className="flex items-start">
                        <Clock className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span>{t('Ταχεία παράδοση σε όλη την Ελλάδα', 'Fast delivery throughout Greece')}</span>
                      </li>
                      <li className="flex items-start">
                        <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span>{t('Τεχνική υποστήριξη για εγκατάσταση', 'Technical support for installation')}</span>
                      </li>
                    </ul>
                    <Button variant="outline" size="lg" asChild>
                      <Link to="/contact">{t('Ζητήστε Προσφορά', 'Request Quote')}</Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Technical Service */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.45, delay: 0.12 }}
                whileHover={{ y: -6 }}
              >
                <Card className="h-full transition-shadow duration-300 hover:shadow-xl">
                  <CardContent className="p-8">
                    <div className="mb-6 p-4 bg-primary/10 rounded-2xl w-fit group">
                      <Wrench className="h-12 w-12 text-primary transition-transform duration-300 group-hover:scale-110" />
                    </div>
                    <h2 className="text-3xl font-bold mb-4">{t('Τεχνική Υποστήριξη', 'Technical Support')}</h2>
                    <p className="text-muted-foreground mb-6">
                      {t('Η ομάδα των πιστοποιημένων τεχνικών μας παρέχει προληπτική συντήρηση, επισκευές και 24/7 τεχνική υποστήριξη για τον εξοπλισμό σας.', 'Our team of certified technicians provides preventive maintenance, repairs and 24/7 technical support for your equipment.')}
                    </p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start">
                        <Phone className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span>{t('Τηλεφωνική υποστήριξη 24/7', '24/7 phone support')}</span>
                      </li>
                      <li className="flex items-start">
                        <Wrench className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span>{t('Προληπτική συντήρηση και επισκευές', 'Preventive maintenance and repairs')}</span>
                      </li>
                      <li className="flex items-start">
                        <Shield className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span>{t('Συμβόλαια συντήρησης προσαρμοσμένα στις ανάγκες σας', 'Maintenance contracts tailored to your needs')}</span>
                      </li>
                    </ul>
                    <Button variant="outline" size="lg" asChild>
                      <Link to="/contact">{t('Επικοινωνήστε', 'Contact')}</Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Seital Technical Support */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.45, delay: 0.24 }}
                whileHover={{ y: -6 }}
              >
                <Card className="h-full border-primary/40 shadow-md ring-1 ring-primary/20 transition-shadow duration-300 hover:shadow-xl">
                  <CardContent className="p-8">
                  <div className="mb-6 flex items-center gap-3 p-4 bg-primary/10 rounded-2xl w-fit group">
                    <Wrench className="h-12 w-12 text-primary flex-shrink-0" />
                    <img
                      src={seitalLogo}
                      alt="Seital"
                      className="h-8 w-auto object-contain transition-transform duration-300 group-hover:scale-110"
                      loading="lazy"
                    />
                  </div>
                    <h2 className="text-3xl font-bold mb-4">{t('Seital Τεχνική Υποστήριξη', 'Seital Technical Support')}</h2>
                    <p className="text-muted-foreground mb-4">
                      {t('Η εξειδικευμένη τεχνική μας ομάδα είναι εδώ για να επιλύει και τα πιο απαιτητικά ζητήματα σε Seital διαχωριστές', 'Our specialized technical team is here to solve even the most demanding issues on Seital separators')}
                    </p>
                    <ul className="space-y-3 my-6">
                      <li className="flex items-start">
                        <Settings className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span>{t('Customized Τεχνική Υποστήριξη', 'Customized Technical Support')}</span>
                      </li>
                      <li className="flex items-start">
                        <Clock className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span>{t('Μακροχρόνια εμπειρία στην επίλυση απαιτητικών προβλημάτων', 'Long-term experience in solving demanding problems')}</span>
                      </li>
                      <li className="flex items-start">
                        <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span>{t('Άμεσοι χρόνοι απόκρισης', 'Immediate response times')}</span>
                      </li>
                    </ul>
                    <Button variant="outline" size="lg" asChild>
                      <Link to="/contact">{t('Επικοινωνήστε', 'Contact')}</Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Why Choose Us */}
            <Card className="bg-gradient-to-br from-primary/5 to-secondary/5">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-6 text-center">{t('Γιατί να Επιλέξετε την Υποστήριξή μας;', 'Why Choose Our Support?')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="mx-auto mb-4 p-3 bg-primary/10 rounded-xl w-fit">
                      <Clock className="h-8 w-8 text-primary" />
                    </div>
                    <h4 className="font-semibold mb-2">{t('Ταχύτητα', 'Speed')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('Άμεση ανταπόκριση σε κάθε αίτημα υποστήριξης', 'Immediate response to every support request')}
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="mx-auto mb-4 p-3 bg-primary/10 rounded-xl w-fit">
                      <Shield className="h-8 w-8 text-primary" />
                    </div>
                    <h4 className="font-semibold mb-2">{t('Εμπειρία', 'Experience')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('Πάνω από 60 χρόνια τεχνογνωσίας στον κλάδο', 'Over 60 years of industry expertise')}
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="mx-auto mb-4 p-3 bg-primary/10 rounded-xl w-fit">
                      <Zap className="h-8 w-8 text-primary" />
                    </div>
                    <h4 className="font-semibold mb-2">{t('Αξιοπιστία', 'Reliability')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('Πιστοποιημένοι τεχνικοί και γνήσια ανταλλακτικά', 'Certified technicians and genuine parts')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Support;